/**
 * The Face ID lock screen for the iOS app. The rules (off until turned on,
 * signed-in only, two minutes away) are in src/native/faceId.js.
 *
 * Locks at launch and when the app comes back after two minutes in the
 * background, and asks for Face ID straight away; if that's cancelled, the
 * screen keeps a button to try again and a way to sign out. While the app is
 * inactive (the app switcher, Control Center) a plain cover hides the trips,
 * except during Defera's own Face ID and permission prompts.
 *
 * App only, and only when the customer turned the lock on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Fingerprint, LockKeyhole, ScanFace } from 'lucide-react';
import { isNativeApp } from '@/native/bootstrap';
import {
  biometricAvailability, faceIdLockEnabled, setFaceIdLock, shouldLock, signedInHere, systemPromptOpen, verifyOwner,
} from '@/native/faceId';

const lockApplies = () => faceIdLockEnabled() && signedInHere();
const ICONS = { 'Face ID': ScanFace, 'Touch ID': Fingerprint, passcode: LockKeyhole };

/** What the locked app shows. Pure, so each state can be tested. */
export function LockScreen({ kind, failed, working, onUnlock, onSignOut }) {
  const Icon = ICONS[kind] || LockKeyhole;
  const method = kind === 'passcode' ? 'your passcode' : kind;
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <img src="/apple-touch-icon.png" alt="" className="h-[72px] w-[72px] rounded-[18px] border border-white/10" />
      <h2 className="mt-6 text-[20px] font-medium text-white">Defera is locked</h2>
      <p className="mt-2 max-w-xs text-[14px] font-normal leading-relaxed text-white/60">
        {failed ? `That didn't work. Try ${method} again.` : `Unlock with ${method} to see your trips.`}
      </p>
      <button
        type="button"
        onClick={onUnlock}
        disabled={working}
        className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 text-[15px] font-medium text-black disabled:opacity-60"
      >
        <Icon className="h-5 w-5" aria-hidden="true" /> Unlock with {kind === 'passcode' ? 'passcode' : kind}
      </button>
      <button type="button" onClick={onSignOut} className="mt-5 text-[13px] font-normal text-white/50 hover:text-white/80">
        Sign out
      </button>
    </div>
  );
}

LockScreen.propTypes = {
  kind: PropTypes.string,
  failed: PropTypes.bool,
  working: PropTypes.bool,
  onUnlock: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
};

export default function FaceIdLock() {
  const [locked, setLocked] = useState(() => isNativeApp && lockApplies());
  const [covered, setCovered] = useState(false);
  const [kind, setKind] = useState('Face ID');
  const [failed, setFailed] = useState(false);
  const [working, setWorking] = useState(false);
  const busy = useRef(false);
  const backgroundedAt = useRef(null);

  const unlock = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setWorking(true);
    setFailed(false);
    const availability = await biometricAvailability();
    if (!availability.available) {
      // No passcode on the phone any more, so nothing to check against
      // (removing it took the passcode). Don't strand the owner.
      setFaceIdLock(false);
      setLocked(false);
    } else {
      setKind(availability.kind);
      if (await verifyOwner()) setLocked(false);
      else setFailed(true);
    }
    busy.current = false;
    setWorking(false);
  }, []);

  const signOut = useCallback(async () => {
    // This phone only: whoever is holding it may not be the owner.
    const [{ forgetThisPhone }, { supabase }] = await Promise.all([import('@/native/push'), import('@/lib/supabase')]);
    await forgetThisPhone();
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    try { localStorage.removeItem('defera.user'); } catch { /* private browsing */ }
    window.location.replace('/');
  }, []);

  // Ask at launch when the app opened locked.
  useEffect(() => {
    if (locked) unlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- launch only
  }, []);

  useEffect(() => {
    if (!isNativeApp) return undefined;
    let removed = false;
    const handles = [];
    import('@capacitor/app').then(async ({ App }) => {
      const added = await Promise.all([
        App.addListener('pause', () => {
          backgroundedAt.current = Date.now();
          if (lockApplies()) setCovered(true);
        }),
        App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            if (lockApplies() && !systemPromptOpen()) setCovered(true);
            return;
          }
          const away = backgroundedAt.current;
          backgroundedAt.current = null;
          if (away != null && shouldLock({ enabled: faceIdLockEnabled(), signedIn: signedInHere(), backgroundedAt: away, now: Date.now() })) {
            setLocked(true);
            unlock();
          }
          setCovered(false);
        }),
      ]);
      if (removed) added.forEach((h) => h.remove());
      else handles.push(...added);
    }).catch(() => {});
    return () => { removed = true; handles.forEach((h) => h.remove()); };
  }, [unlock]);

  // Keep the page underneath out of reach of taps and VoiceOver while locked.
  useEffect(() => {
    const root = isNativeApp ? document.getElementById('root') : null;
    if (!root) return undefined;
    if (locked) root.setAttribute('inert', '');
    else root.removeAttribute('inert');
    return () => root.removeAttribute('inert');
  }, [locked]);

  if (!isNativeApp || (!locked && !covered)) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-black pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      role={locked ? 'dialog' : undefined}
      aria-modal={locked ? 'true' : undefined}
      aria-label={locked ? 'Defera is locked' : undefined}
    >
      {locked ? (
        <LockScreen kind={kind} failed={failed} working={working} onUnlock={unlock} onSignOut={signOut} />
      ) : (
        <div className="flex h-full items-center justify-center">
          <img src="/apple-touch-icon.png" alt="" className="h-[72px] w-[72px] rounded-[18px]" />
        </div>
      )}
    </div>,
    document.body,
  );
}
